/**
 * Build PersonalizeDataGrid / list default columns from backend metadata
 * and/or raw API row keys (not FormConfigurations hardcodes).
 */

function estimateColumnWidth(label, minWidth = 120, maxWidth = 320) {
  const text = String(label || "").trim();
  const estimated = text.length * 9 + 48;
  return Math.min(maxWidth, Math.max(minWidth, estimated));
}

export function titleCaseFieldKey(key) {
  return String(key || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function isLikelyIdFieldKey(key) {
  const k = String(key || "").toLowerCase();
  return k === "id" || k.endsWith("_id") || k.startsWith("__");
}

/**
 * Restrict saved/backend column configs to keys allowed for this module.
 * When allowedKeys is empty or omitted, all columns are returned unchanged.
 */
export function filterColumnsByAllowedKeys(columns = [], allowedKeys = null) {
  const list = Array.isArray(columns) ? columns : [];
  if (!Array.isArray(allowedKeys) || allowedKeys.length === 0) {
    return list;
  }

  const allowed = new Set(allowedKeys.map((key) => String(key)));
  return list.filter((col) => {
    const fieldKey = col?.field || col?.key;
    return fieldKey != null && allowed.has(String(fieldKey));
  });
}

function scalarKeysFromRow(row) {
  if (!row || typeof row !== "object") return [];
  return Object.keys(row).filter((key) => {
    if (key.startsWith("__")) return false;
    const value = row[key];
    return value == null || typeof value !== "object";
  });
}

function resolveMetadataFieldKey(field) {
  return (
    field?.ui_field_key ||
    field?.field_key ||
    field?.column_name ||
    field?.field ||
    null
  );
}

/**
 * @param {object} options
 * @param {Array<object>} [options.metadataFields] - fields from GET /api/metadata/{module}
 * @param {object|null} [options.sampleRow] - first raw API row for key discovery
 * @param {number} [options.fallbackVisibleCount=5] - visible count when no is_display_field
 * @returns {Array<{key, field, label, width, visible, order, required}>}
 */
export function buildBackendListColumns({
  metadataFields = [],
  sampleRow = null,
  fallbackVisibleCount = 5,
} = {}) {
  const labelByKey = new Map();
  const orderByKey = new Map();
  const displayKeys = [];

  const fields = Array.isArray(metadataFields) ? metadataFields : [];
  fields.forEach((field, index) => {
    const key = resolveMetadataFieldKey(field);
    if (!key) return;
    if (field?.label) labelByKey.set(key, field.label);
    const order =
      field?.filter_order != null && field.filter_order !== ""
        ? Number(field.filter_order)
        : index;
    if (!Number.isNaN(order)) orderByKey.set(key, order);
    if (
      field?.is_display_field === 1 ||
      field?.is_display_field === true ||
      field?.is_display_field === "1"
    ) {
      displayKeys.push(key);
    }
  });

  const metadataKeys = fields.map(resolveMetadataFieldKey).filter(Boolean);

  // Prefer API sample-row keys first so fallback matches admin Data Grid
  // Configuration field discovery (TableConfigForm scalar Object.keys).
  const rowKeys = scalarKeysFromRow(sampleRow);
  const allKeys =
    rowKeys.length > 0
      ? [...new Set([...rowKeys, ...metadataKeys])]
      : [...new Set(metadataKeys)];

  if (allKeys.length === 0) return [];

  const originalIndex = new Map(allKeys.map((key, index) => [key, index]));
  const preferApiKeyOrder = rowKeys.length > 0;

  if (!preferApiKeyOrder) {
    allKeys.sort((a, b) => {
      const oa = orderByKey.has(a) ? orderByKey.get(a) : Number.MAX_SAFE_INTEGER;
      const ob = orderByKey.has(b) ? orderByKey.get(b) : Number.MAX_SAFE_INTEGER;
      if (oa !== ob) return oa - ob;
      return (originalIndex.get(a) ?? 0) - (originalIndex.get(b) ?? 0);
    });
  }

  let visibleKeys;
  if (displayKeys.length > 0) {
    visibleKeys = displayKeys.filter((key) => allKeys.includes(key));
  } else {
    visibleKeys = allKeys
      .filter((key) => !isLikelyIdFieldKey(key))
      .slice(0, fallbackVisibleCount);
    if (visibleKeys.length === 0) {
      visibleKeys = allKeys.slice(0, fallbackVisibleCount);
    }
  }

  return allKeys.map((key, index) => {
    const label = labelByKey.get(key) || titleCaseFieldKey(key);
    const visibleIndex = visibleKeys.indexOf(key);
    return {
      key,
      field: key,
      label,
      width: estimateColumnWidth(label),
      visible: visibleIndex >= 0,
      order: visibleIndex >= 0 ? visibleIndex : index,
      required: false,
    };
  });
}

/**
 * Normalize a column into the shared list/personalize shape.
 */
function normalizeLayoutColumn(col, index = 0) {
  if (!col || typeof col !== "object") return null;
  const fieldKey = col.field ?? col.key;
  if (fieldKey == null || fieldKey === "") return null;
  const key = String(fieldKey);
  const pinned =
    col.pinned === true || col.pinned === "left" ? "left" : col.pinned || undefined;

  return {
    ...col,
    key,
    field: key,
    label: col.label || titleCaseFieldKey(key),
    width: Number(col.width) || 150,
    visible: !!col.visible,
    filterable: col.filterable !== false,
    sortable: col.sortable !== false,
    pinned,
    required: !!(col.required || col.mandatory),
    order: col.order ?? index,
  };
}

/**
 * Merge saved personalize/org prefs onto a general (admin) catalog.
 *
 * Catalog keys define the universe and default order. Saved prefs overlay
 * visibility, width, pin, label, and relative order for matching keys.
 * Keys only present in saved (stale) are dropped. Catalog-only keys keep
 * catalog defaults so new admin fields appear after personalize.
 *
 * When catalog is empty, saved columns are normalized and returned as-is.
 *
 * @param {Array<object>} catalogColumns
 * @param {Array<object>} savedColumns
 * @returns {Array<object>}
 */
export function mergeColumnLayouts(catalogColumns = [], savedColumns = []) {
  const catalog = (Array.isArray(catalogColumns) ? catalogColumns : [])
    .map((col, index) => normalizeLayoutColumn(col, index))
    .filter(Boolean);
  const saved = (Array.isArray(savedColumns) ? savedColumns : [])
    .map((col, index) => normalizeLayoutColumn(col, index))
    .filter(Boolean);

  if (catalog.length === 0) {
    return saved
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((col, index) => ({ ...col, order: index }));
  }

  const savedByKey = new Map(saved.map((col) => [col.key, col]));
  const savedOrder = new Map(
    saved
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((col, index) => [col.key, index])
  );

  const merged = catalog.map((base, catalogIndex) => {
    const overlay = savedByKey.get(base.key);
    if (!overlay) {
      return {
        ...base,
        order: Number.MAX_SAFE_INTEGER / 2 + catalogIndex,
      };
    }

    return {
      ...base,
      ...overlay,
      key: base.key,
      field: base.key,
      label: overlay.label || base.label,
      width: Number(overlay.width) || base.width || 150,
      visible: overlay.visible,
      filterable:
        overlay.filterable !== undefined
          ? !!overlay.filterable
          : base.filterable !== false,
      sortable:
        overlay.sortable !== undefined
          ? !!overlay.sortable
          : base.sortable !== false,
      pinned:
        overlay.pinned === true || overlay.pinned === "left"
          ? "left"
          : overlay.pinned || undefined,
      required: !!(overlay.required || base.required),
      order: savedOrder.has(base.key)
        ? savedOrder.get(base.key)
        : Number.MAX_SAFE_INTEGER / 2 + catalogIndex,
    };
  });

  return merged
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((col, index) => ({ ...col, order: index }));
}

/**
 * Convert backend-built columns into list config fields used by TableDataGeneric.
 */
export function backendColumnsToListConfig(columns = []) {
  const list = Array.isArray(columns) ? columns : [];
  return {
    defaultVisibleColumns: list
      .filter((c) => c.visible)
      .map((c) => c.key || c.field),
    columnLabels: Object.fromEntries(
      list.map((c) => [
        c.key || c.field,
        c.label || titleCaseFieldKey(c.key || c.field),
      ])
    ),
    columnWidths: Object.fromEntries(
      list.map((c) => [c.key || c.field, c.width || 180])
    ),
    personalizeColumnKeys: list.map((c) => c.key || c.field),
  };
}
